import React from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import OfferFinancingStructureSummary from './OfferFinancingStructureSummary';
import FacilityOfferFinancingStructureSummary from './FacilityOfferFinancingStructureSummary';
import ContractModificationFinancingStructureSummary from './ContractModificationFinancingStructureSummary';
import OfferFinancingStructureComponent from './OfferFinancingStructureComponent';
import FacilityOfferFinancingStructureComponent from './FacilityOfferFinancingStructureComponent';
import ContractModificationFinancingStructureComponent from './ContractModificationFinancingStructureComponent';
import EuroSymbol from '@mui/icons-material/EuroSymbol';
import { withRights } from 'providers/RightsConsumer';
import { emptyArray } from 'constants/emptyConst';
import { interestType } from 'features/offer/constants';
import CollapseOrBlankCard from 'components/common/CollapseOrBlankCard';
import WrappedFakeText from 'components/common/WrappedFakeText';

class OfferFinancingStructureCard extends React.PureComponent {
  state = {
    expanded: false,
    prevProps: {
      saving: this.props.saving
    },
    showFinancialProductChangeModal: false
  };

  toggleExpanded = () => {
    if (!this.props.rights.expandFinancingStructure) {
      return;
    }

    this.setState(prevState => ({ expanded: !prevState.expanded }));
  };

  toggleFinancialProductChange = () =>
    this.setState(prevState => ({
      ...prevState,
      showFinancialProductChangeModal: !prevState.showFinancialProductChangeModal
    }));

  render() {
    const {
      t,
      financingStructure,
      isDirty,
      validationInfo,
      financingStructureLoading,
      maxResidualValuePercentLoading,
      onPropertyChange,
      onSave,
      saving,
      onOpenNegotiationModal,
      offerFinancialProductRuleReadOnlyFields,
      areGuaranteeRatesVisible,
      isContractModification,
      isFacilityFrameOffer,
      interestTypes,
      interestTypesIsLoaded,
      contractTypes,
      referenceInterests,
      priceTypes,
      documentId,
      siteId
    } = this.props;

    const { expanded } = this.state;
    const { expandFinancingStructure } = this.props.rights;
    const fixedInterest = interestTypes?.find(int => int.code === interestType.FIX);
    const variableInterest = interestTypes?.find(int => int.code === interestType.VAR);
    const interestTypeName = !interestTypesIsLoaded ? '' : financingStructure.isFixed ? fixedInterest.name : variableInterest.name;

    return (
      <CollapseOrBlankCard
        id={'offerFinancingStructureCard'}
        icon={EuroSymbol}
        title={t('OfferEdit.FinancingStructure.Title')}
        expanded={expanded}
        canExpand={expandFinancingStructure}
        onToggle={this.toggleExpanded}
        subheader={
          !financingStructureLoading &&
          (isContractModification ? (
            <ContractModificationFinancingStructureSummary document={financingStructure} />
          ) : isFacilityFrameOffer ? (
            <FacilityOfferFinancingStructureSummary document={financingStructure} />
          ) : (
            <OfferFinancingStructureSummary document={financingStructure} />
          ))
        }
        hideSubheaderOnExpand
      >
        {financingStructureLoading ? (
          <WrappedFakeText lines={6} />
        ) : isContractModification ? (
          <ContractModificationFinancingStructureComponent
            document={financingStructure}
            isDirty={isDirty}
            validationInfo={validationInfo}
            onPropertyChange={onPropertyChange}
            onSave={onSave}
            saving={saving}
            maxResidualValuePercentLoading={maxResidualValuePercentLoading}
            offerFinancialProductRuleReadOnlyFields={offerFinancialProductRuleReadOnlyFields}
            areGuaranteeRatesVisible={areGuaranteeRatesVisible}
            onFinancialProductChangeModalToggle={this.toggleFinancialProductChange}
            showFinancialProductChangeModal={this.state.showFinancialProductChangeModal}
            documentId={documentId}
            siteId={siteId}
          />
        ) : !isFacilityFrameOffer ? (
          <OfferFinancingStructureComponent
            document={financingStructure}
            isDirty={isDirty}
            validationInfo={validationInfo}
            onPropertyChange={onPropertyChange}
            onSave={onSave}
            saving={saving}
            onOpenNegotiationModal={onOpenNegotiationModal}
            maxResidualValuePercentLoading={maxResidualValuePercentLoading}
            offerFinancialProductRuleReadOnlyFields={offerFinancialProductRuleReadOnlyFields}
            areGuaranteeRatesVisible={areGuaranteeRatesVisible}
            isContractModification={isContractModification}
            isFacilityFrameOffer={isFacilityFrameOffer}
            priceTypes={priceTypes}
            interestTypeName={interestTypeName}
          />
        ) : (
          <FacilityOfferFinancingStructureComponent
            document={financingStructure}
            isDirty={isDirty}
            validationInfo={validationInfo}
            onPropertyChange={onPropertyChange}
            onSave={onSave}
            saving={saving}
            maxResidualValuePercentLoading={maxResidualValuePercentLoading}
            offerFinancialProductRuleReadOnlyFields={offerFinancialProductRuleReadOnlyFields}
            isFacilityFrameOffer={isFacilityFrameOffer}
            interestTypes={interestTypes}
            contractTypes={contractTypes}
            referenceInterests={referenceInterests || emptyArray}
            priceTypes={priceTypes}
          />
        )}
      </CollapseOrBlankCard>
    );
  }
}

const customValidationPropReferenceInterests = (props, propName) => {
  if (!props['isContractModification'] && props['isFacilityFrameOffer'] && !props[propName])
    return new Error(
      'Failed prop type: The prop `referenceInterests` is marked as required in `OfferFinancingStructureCard`, but its value is `' +
        typeof props[propName] +
        '`.'
    );
};

OfferFinancingStructureCard.propTypes = {
  t: PropTypes.func.isRequired,
  financingStructure: PropTypes.object.isRequired,
  isDirty: PropTypes.bool.isRequired,
  validationInfo: PropTypes.object.isRequired,
  financingStructureLoading: PropTypes.bool.isRequired,
  maxResidualValuePercentLoading: PropTypes.bool.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  onOpenNegotiationModal: PropTypes.func.isRequired,
  rights: PropTypes.object.isRequired,
  offerFinancialProductRuleReadOnlyFields: PropTypes.object.isRequired,
  areGuaranteeRatesVisible: PropTypes.bool.isRequired,
  isContractModification: PropTypes.bool.isRequired,
  isFacilityFrameOffer: PropTypes.bool.isRequired,
  interestTypes: PropTypes.array,
  interestTypesIsLoaded: PropTypes.bool,
  contractTypes: PropTypes.array,
  referenceInterests: customValidationPropReferenceInterests,
  priceTypes: PropTypes.array,
  documentId: PropTypes.number,
  siteId: PropTypes.number
};

export default withTranslation()(withRights(OfferFinancingStructureCard));
